import React, { useState, useMemo, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Button,
  TablePagination,
  Chip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useAppDispatch } from "../../../../Redux/Store/Hooks";
import { StudyMaterialPreview } from "../../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialPreview";
import StudyMaterialPreviewDialog from "../PreviewDialog/PreviewDialod";
import EmptyCart from "../../assets/images/Empty_Cart.svg";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

export interface TableColumn<T> {
  key: keyof T;
  title: string;
  sortable?: boolean;
  width?: string | number;
  align?: "left" | "center" | "right";
  render?: (value: T[keyof T], record: T, index: number) => ReactNode;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (record: T, index: number) => void;
  rowClassName?: (record: T, index: number) => string;
  onQuantityChange?: (record: T, index: number, newQuantity: number) => void;
  pageType?: "cart" | "details" | string; //
  getPreviewId?: (record: T) => string | number | null; // <-- NEW
}

type SortOrder = "asc" | "desc" | null;

// Function to get chip styles based on date value
const getDateChipProps = (value: string) => {
  const lowerValue = value.toLowerCase();

  if (lowerValue.includes("today")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#E8F5E8",
        color: "#2E7D32",
        border: "1px solid #A5D6A7",
        fontWeight: 500,
      },
    };
  } else if (lowerValue.includes("yesterday")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#FFF3E0",
        color: "#E65100",
        border: "1px solid #FFCC02",
        fontWeight: 500,
      },
    };
  } else if (lowerValue.includes("week")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#F5F5F5",
        color: "#424242",
        border: "1px solid #BDBDBD",
        fontWeight: 500,
      },
    };
  } else if (lowerValue.includes("month")) {
    return {
      label: value,
      sx: {
        backgroundColor: "#E3F2FD",
        color: "#1565C0",
        border: "1px solid #90CAF9",
        fontWeight: 500,
      },
    };
  } else {
    return {
      label: value,
      sx: {
        backgroundColor: "#F5F5F5",
        color: "#424242",
        border: "1px solid #BDBDBD",
        fontWeight: 500,
      },
    };
  }
};

const Table = <T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  rowClassName,
  onQuantityChange,
  getPreviewId,
  pageType,
}: TableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: SortOrder;
  }>({ key: null, direction: null });

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPreviewData, setSelectedPreviewData] = useState<T | null>(
    null
  );

  const dispatch = useAppDispatch();

  const handleSort = (key: keyof T) => {
    let direction: SortOrder = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    else if (sortConfig.key === key && sortConfig.direction === "desc")
      direction = null;
    setSortConfig({ key: direction ? key : null, direction });
  };

  const handlePreviewClick = (row: T) => {
    // Try getPreviewId first, then fall back
    const previewId: string | number | null =
      (getPreviewId ? getPreviewId(row) : undefined) ??
      (row as any).id ??
      (row as any).cart_id ??
      null;

    if (!previewId) {
      console.warn("No preview ID found for row:", row);
      return;
    }

    console.log("Preview ID:", previewId);

    dispatch(StudyMaterialPreview({ id: previewId }));
    setSelectedPreviewData(row);
    setPreviewDialogOpen(true);
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setSelectedPreviewData(null);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key!];
      const bVal = b[sortConfig.key!];
      if (typeof aVal === "number" && typeof bVal === "number")
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      if (typeof aVal === "string" && typeof bVal === "string")
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return 0;
    });
  }, [data, sortConfig]);

useEffect(() => {
  const totalPages = Math.ceil(data.length / rowsPerPage);
  // If current page has no data, move to last page
  if (page > 0 && page * rowsPerPage >= data.length) {
    setPage(Math.max(totalPages - 1, 0));
  }
  // If no data at all, reset to page 0
  if (data.length === 0 && page !== 0) {
    setPage(0);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [data]);


const paginatedData = useMemo(() => {
  const start = page * rowsPerPage;
  return sortedData.slice(start, start + rowsPerPage);
}, [sortedData, page]);

  const getSortIcon = (columnKey: keyof T) => {
    if (sortConfig.key !== columnKey || !sortConfig.direction)
      return <UnfoldMoreIcon fontSize="small" sx={{ color: "white" }} />;
    if (sortConfig.direction === "asc")
      return <ArrowUpwardIcon fontSize="small" sx={{ color: "white" }} />;
    return <ArrowDownwardIcon fontSize="small" sx={{ color: "white" }} />;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        component={Paper}
        elevation={1}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={1}
        sx={{ border: "1px solid #e0e0e0", borderRadius: "0px", width: "100%" }}
      >
        <TableContainer sx={{ maxHeight: 580 }}>
          <MuiTable
            stickyHeader
            sx={{
              borderCollapse: "collapse",
              "& th, & td": {
                border: "1px solid #e0e0e0",
                textAlign: "center",
                fontSize: "14px",
                fontWeight: 500,
                padding: "8px 12px",
                height: "44px",
                verticalAlign: "middle",
                lineHeight: 1, // <--- ensures no baseline offset
              },
              "& th": {
                backgroundColor: "#009DFF",
                color: "white",
                position: "sticky",
                top: "-2px",
                zIndex: 1,
                height: "44px",
                lineHeight: 1,
                paddingTop: "10px", // <--- balances text inside
                paddingBottom: "10px",
              },
              // key fix for the sticky-header visual gap
              "& .MuiTableCell-stickyHeader": {
                backgroundClip: "padding-box", // prevents bleed
                boxShadow: "inset 0 -1px 0 #e0e0e0", // closes the thin gap line visually
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#009DFF" }}>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "white",
                    border: "1px solid #fff",
                    position: "sticky",
                    height: "100%",
                    backgroundColor: "#009DFF",
                    zIndex: 1,
                    width: "60px",
                  }}
                >
                  S.No
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align ?? "center"}
                    style={{ width: column.width }}
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      color: "white",
                      border: "1px solid #fff",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#009DFF",
                      zIndex: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {column.title}
                      {column.sortable && (
                        <IconButton
                          size="small"
                          onClick={() => handleSort(column.key)}
                          sx={{ color: "white", ml: 0.5 }}
                        >
                          {getSortIcon(column.key)}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody sx={{ fontSize: "14px" }}>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={(row as any).id || index}
                    hover
                    onClick={() => onRowClick?.(row, index)}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      "&:nth-of-type(even)": { backgroundColor: "none" },
                    }}
                    className={rowClassName ? rowClassName(row, index) : ""}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    {columns.map((column) => {
                      let content: ReactNode = null;
                      const value = row[column.key];

                      if (column.key === "fileType") {
                        content = (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                          >
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "500" }}
                            >
                              {String(value ?? "")}
                            </Typography>
                            <PictureAsPdfIcon color="error" fontSize="small" />
                          </Box>
                        );
                      } else if (column.key === "addCart") {
                        // if (column.key === "addCart") {
                        content = (
                          <Checkbox
                            checked={Boolean(value)}
                            onChange={() => console.log("Cart toggled", row)}
                          />
                        );
                      } else if (
                        column.key === "lastUpdate" ||
                        column.key === "modifiedTime" ||
                        column.key === "date" ||
                        column.key === "updated_on"
                      ) {
                        if (value && typeof value === "string") {
                          const chipProps = getDateChipProps(value);
                          content = <Chip {...chipProps} size="small" />;
                        } else {
                          content = value as ReactNode;
                        }
                      } else if (column.key === "preview") {
                        content = (
                          <IconButton
                            size="small"
                            sx={{ color: "#009DFF" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewClick(row);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        );
                      } else if (
                        column.key === "quantity" ||
                        column.key === "Quantity"
                      ) {
                        const isEditable = (row as any).editable !== false;
                        const numericValue =
                          typeof value === "number"
                            ? value
                            : parseInt(value as string, 10) || 0;

                        content = isEditable ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap="8px"
                            justifyContent="center"
                          >
                            <IconButton
                              sx={{
                                borderRadius: 1,
                                backgroundColor: "#009DFF",
                                maxWidth: "30px",
                                color: "white",
                                "&:hover": { backgroundColor: "#0082CC" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = Math.max(0, numericValue - 1);
                                onQuantityChange?.(row, index, newValue);
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Box
                              sx={{
                                backgroundColor: "#D9D9D9",
                                border: "1px solid #BDBDBD",
                                borderRadius: "4px",
                                px: 1,
                                py: 0.5,
                                minWidth: "100px",
                                textAlign: "center",
                                fontWeight: 500,
                              }}
                            >
                              {numericValue}
                            </Box>
                            <IconButton
                              sx={{
                                borderRadius: 1,
                                backgroundColor: "#009DFF",
                                maxWidth: "30px",
                                color: "white",
                                "&:hover": { backgroundColor: "#0082CC" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = numericValue + 1;
                                onQuantityChange?.(row, index, newValue);
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography>{numericValue}</Typography>
                        );
                      } else if (
                        column.key === "add to cart" ||
                        column.key === "Add to Cart"
                      ) {
                        const isChecked = Boolean(
                          (row as any).quantity && (row as any).quantity > 0
                        );

                        content = (
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const newValue = checked ? 1 : 0;
                              onQuantityChange?.(
                                { ...row, status: checked },
                                index,
                                newValue
                              );
                            }}
                          />
                        );
                      } else if (
                        column.key === "print" ||
                        column.key === "action"
                      ) {
                        content = (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              backgroundColor: "#F5F5F5",
                              color: "#424242",
                              border: "1px solid #BDBDBD",
                              "&:hover": { backgroundColor: "#EEEEEE" },
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "14px",
                            }}
                          >
                            {typeof value === "string" ? value : "Take Print"}
                          </Button>
                        );
                      } else if (column.render) {
                        content = column.render(value, row, index);
                      } else {
                        content = value as ReactNode;
                      }

                      return (
                        <TableCell
                          key={String(column.key)}
                          align={column.align ?? "center"}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : pageType === "cart" || pageType === "details" ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={2}
                    >
                      <img
                        src={EmptyCart}
                        alt="Empty Cart"
                        style={{ width: "150px", marginBottom: "16px" }}
                      />
                      {/* <Typography variant="subtitle1" color="textSecondary">
                        Your cart is empty
                      </Typography> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`empty-${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    {columns.map((col) => (
                      <TableCell
                        key={`empty-${i}-${String(col.key)}`}
                        align={col.align ?? "center"}
                      >
                        <Box sx={{ height: 25 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : null}
            </TableBody>
          </MuiTable>
        </TableContainer>
        {data.length > 10 && (
          <Box display="flex" alignItems="center" p={2} gap={"2px"}>
            <Box>
              <IconButton
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page === 0}
                size="small"
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() =>
                  setPage((prev) =>
                    Math.min(prev + 1, Math.ceil(data.length / rowsPerPage) - 1)
                  )
                }
                disabled={page >= Math.ceil(data.length / rowsPerPage) - 1}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography sx={{ fontSize: "12px", fontWeight: "600" }}>
              Page {page + 1} of {Math.ceil(data.length / rowsPerPage)}
            </Typography>
          </Box>
        )}
      </Paper>
      <StudyMaterialPreviewDialog
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
      />
    </>
  );
};

export default Table;
